import { useState, useEffect } from 'react';
import Title from './Title';
import SubTitle from './SubTitle';
import { useAuth } from '../service/AuthContext';
import CircleIcon from '@mui/icons-material/Circle';
import { db, functions } from '../service/Firebase-config';
import { updateDoc, doc, deleteField } from 'firebase/firestore';
import LoadingButton from '@mui/lab/LoadingButton';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  DialogContentText,
  TextField,
  Box,
  Chip,
  Tooltip
} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import { useGlobalContext } from '../context/GlobalContext';
import { httpsCallable } from 'firebase/functions';

export default function InfoBox({ expectedRole = '' }) {
  const { selectedUser, setSelectedUser, totalStars, setTotalStars } = useGlobalContext();
  const { userInfo } = useAuth();
  const [confirmAction, setConfirmAction] = useState(null);
  const [isApproveLoading, setIsApproveLoading] = useState(false);
  const [isRemoveLoading, setIsRemoveLoading] = useState(false);
  const [isStarLoading, setIsStarLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [initialStars, setInitialStars] = useState(totalStars);

  useEffect(() => {
    setIsEditing(false); // Exit edit mode when value changes
  }, [selectedUser]);

  const handleClaim = async () => {
    if (totalStars >= 0) {
      setIsStarLoading(true);
      try {
        await updateDoc(doc(db, 'students', selectedUser?.uid), { totalStars });
        setIsEditing(false); // Exit edit mode after saving
      } catch (error) {
        console.error("Error updating document: ", error);
      } finally {
        setIsStarLoading(false);
      }
    }
  };

  const handleStarsChange = (event) => {
    const newTotalStars = parseInt(event.target.value, 10);
    setTotalStars(newTotalStars >= 0 ? newTotalStars : 0);
  };

  const handleCancel = () => {
    setTotalStars(initialStars); // Revert to the initial value
    setIsEditing(false); // Exit edit mode
  };

  const handleEdit = () => {
    setInitialStars(totalStars); // Save the initial value
    setIsEditing(true);
  };

  const handleCloseConfirm = () => {
    if (isApproveLoading || isRemoveLoading) return;

    setConfirmAction(null);
  };

  const handleConfirm = async () => {
    if (!selectedUser?.uid || !confirmAction) return;

    const action = confirmAction;

    if (action === 'approve') {
      setIsApproveLoading(true);
    } else {
      setIsRemoveLoading(true);
    }

    try {
      if (action === 'approve') {
        await updateDoc(
          doc(db, 'students', selectedUser.uid),
          {
            isEnrolled: deleteField(),
          }
        );

        setSelectedUser((prev) => {
          if (!prev) return prev;

          const updatedUser = { ...prev };
          delete updatedUser.isEnrolled;

          return updatedUser;
        });
      }

      if (action === 'remove') {
        const deleteUser = httpsCallable(
          functions,
          'handleDeleteUser'
        );

        await deleteUser({
          uid: selectedUser.uid,
          isStudent: selectedUser.role === 'student',
        });

        setSelectedUser(null);
      }

      setConfirmAction(null);
    } catch (error) {
      console.error(
        `Error ${
          action === 'approve' ? 'approving' : 'removing'
        } user:`,
        error
      );

      window.alert(
        action === 'approve'
          ? 'Failed to approve the user.'
          : 'Failed to delete the account.'
      );
    } finally {
      if (action === 'approve') {
        setIsApproveLoading(false);
      } else {
        setIsRemoveLoading(false);
      }
    }
  };

  return (
    <>
      <Box
        display="column"
        sx={{
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          textOverflow: 'ellipsis',
          width: '100%', 
        }}
      >
        <Title>
          <Box
            display="flex"
            alignItems="center"
            gap={2}
            sx={{ color: '#000000' }}
          >
            <Box sx={{ fontSize: 30 }}>
              Dr Song Square Academy
            </Box>

            {selectedUser?.isEnrolled === false && (
              <>
                <Button
                  variant="contained"
                  color="info"
                  size="small"
                  disabled={isApproveLoading || isRemoveLoading}
                  onClick={() => setConfirmAction('approve')}
                >
                  Approve
                </Button>

                <Button
                  variant="contained"
                  color="error"
                  size="small"
                  disabled={isApproveLoading || isRemoveLoading}
                  onClick={() => setConfirmAction('remove')}
                >
                  Remove
                </Button>
              </>
            )}
           {/* {selectedUser?.active === false && (
              <Button
                variant="contained"
                color="error"
                size="small"
                disabled={isApproveLoading || isRemoveLoading}
                onClick={() => setConfirmAction('remove')}
              >
                Remove
              </Button>
            )} */}
          </Box>
          <Box display="flex" alignItems="center">
            <b>Name</b> {':  '} {selectedUser?.displayName}
            {selectedUser && userInfo?.role !== 'student' && (
              <>(<a href={`mailto:${selectedUser.email}`} style={{ textDecoration: 'none', fontWeight: 'normal', color: 'inherit' }}>{selectedUser.email}</a>)</>
            )}
            {selectedUser && (
              <Box display="flex" alignItems="center" ml={1}>
                {selectedUser?.isEnrolled === false ? (
                  <Chip
                    label={"New"}
                    variant="default"
                    color="error"
                    size="small"
                  />
                )
                :(
                  <Tooltip title={selectedUser.active ? 'Active' : 'Inactive'}>
                    <CircleIcon sx={{ color: selectedUser.active ? '#5Dbb63' : '#FF0000', fontSize: 15 }} />
                  </Tooltip>
                )}
              </Box>
            )}
          </Box>
        </Title>
        {selectedUser?.role === 'student' && (
          <>
            <SubTitle>
              <b>School</b> {':  '} {selectedUser?.school} ({selectedUser?.grade})
            </SubTitle>
            <SubTitle>
              <Box display="flex" alignItems="center">
                <b>Total Stars</b> {':  '}
                <StarIcon sx={{ color: "#FFC300" }} />
                {expectedRole === 'admin' ? (
                  isEditing ? (
                    <>
                      <TextField
                        value={totalStars}
                        onChange={handleStarsChange}
                        type="number"
                        size="small"
                        variant="outlined"
                        sx={{ width: 80, ml: 1 }}
                        InputProps={{
                          inputProps: { min: 0 },
                          style: { fontSize: '0.875rem' }
                        }}
                      />
                      <LoadingButton
                        loading={isStarLoading}
                        onClick={handleClaim}
                        variant="contained"
                        size="small"
                        sx={{ ml: 2, backgroundColor: 'green', '&:hover': { backgroundColor: 'darkgreen' } }}
                      >
                        Save
                      </LoadingButton>
                      <Button
                        onClick={handleCancel}
                        variant="contained"
                        size="small"
                        sx={{ ml: 1, backgroundColor: 'grey', '&:hover': { backgroundColor: 'darkgrey' } }}
                      >
                        Cancel
                      </Button>
                    </>
                  ) : (
                    <>
                      {totalStars}
                      <Button onClick={handleEdit} variant="contained" size="small" sx={{ ml: 2 }}>
                        Edit
                      </Button>
                    </>
                  )
                ) : (
                  totalStars
                )}
              </Box>
            </SubTitle>
          </>
        )}
        {userInfo?.role !== 'student' && (
          <SubTitle>
            <b>Info</b> {':  '} {selectedUser?.info}
          </SubTitle>
        )}
        <SubTitle>
          <b>Branch</b> {':  '} {selectedUser?.branch}
        </SubTitle>
      </Box>
      <Dialog
        open={confirmAction !== null}
        onClose={handleCloseConfirm}
        aria-labelledby="confirmation-dialog-title"
        aria-describedby="confirmation-dialog-description"
      >
        <DialogTitle id="confirmation-dialog-title">
          {confirmAction === 'approve'
            ? 'Approve User'
            : 'Delete Account'}
        </DialogTitle>

        <DialogContent>
          <DialogContentText id="confirmation-dialog-description">
            {confirmAction === 'approve'
              ? `Are you sure you want to approve ${
                  selectedUser?.displayName || 'this user'
                }?`
              : `Are you sure you want to permanently delete ${
                  selectedUser?.displayName || 'this user'
                }? This action cannot be undone.`}
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={handleCloseConfirm}
            disabled={isApproveLoading || isRemoveLoading}
          >
            Cancel
          </Button>

          <LoadingButton
            onClick={handleConfirm}
            loading={
              confirmAction === 'approve'
                ? isApproveLoading
                : isRemoveLoading
            }
            variant="contained"
            color={
              confirmAction === 'approve'
                ? 'info'
                : 'error'
            }
            autoFocus
          >
            {confirmAction === 'approve'
              ? 'Approve'
              : 'Delete Account'}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
}
